import { fetchUtils } from 'react-admin';
import { stringify } from 'query-string';
import { processFiles } from './imageUpload';

const apiUrl = process.env.REACT_APP_API_URL;

const httpClient = (url, options = {}) => {
  if (!options.headers) {
    options.headers = new Headers({ Accept: 'application/json' });
  }
  const token = localStorage.getItem('token');
  options.headers.set('Authorization', `Bearer ${token}`);
  return fetchUtils.fetchJson(url, options);
};

const dataProvider = {
  getList: (resource, params) => {
    const { page, perPage } = params.pagination;
    const { field, order } = params.sort;

    let query = {};

    // Si el recurso NO es 'provinces', construimos la query con paginación y filtros
    if (resource !== "provinces") {
      const skip = (page - 1) * perPage;
      const limit = perPage;
      const sort = `${field} ${order}`;
      query = {
        limit,
        skip,
        sort,
        where: JSON.stringify(params.filter),
      };
    }
    // Para 'provinces', la query se queda vacía (sin parámetros adicionales)

    const url = `${apiUrl}/${resource}?${stringify(query)}`;
    console.log(`DataProvider.getList - Resource: ${resource}, URL: ${url}`);

    return httpClient(url).then(({ headers, json }) => {
      // Para 'provinces', el backend devuelve un array simple, no un objeto con 'data' y 'total'.
      // Y no envía 'content-range'.
      if (resource === "provinces") {
        return {
          data: json, // El json es directamente el array de provincias
          total: json.length, // El total es la longitud del array
        };
      }

      // Lógica original para otros recursos
      const contentRange = headers.get("content-range");
      let total = 0;
      if (contentRange) {
        // Extraer el total del formato "items 0-24/66"
        const rangeParts = contentRange.split("/");
        if (rangeParts.length > 1) {
          total = parseInt(rangeParts[1], 10);
        } else {
          // Si el formato es solo un número (ej. "66"), lo usamos directamente
          total = parseInt(contentRange, 10);
        }
        if (isNaN(total)) {
          console.warn(
            "Content-Range header found but total could not be parsed:",
            contentRange
          );
          total = json.length; // Fallback si el parseo falla
        }
      } else {
        console.warn(
          "No Content-Range header found, using json.length as total. Resource:",
          resource
        );
        total = json.length; // Fallback si no hay content-range
      }

      return {
        data: json,
        total,
      };
    });
  },

  getOne: (resource, params) =>
    httpClient(`${apiUrl}/${resource}/${params.id}`).then(({ json }) => ({
      data: json,
    })),

  getMany: (resource, params) => {
    const query = {
      filter: JSON.stringify({ id: params.ids }),
    };
    const url = `${apiUrl}/${resource}?${stringify(query)}`;
    return httpClient(url).then(({ json }) => ({ data: json }));
  },

  getManyReference: (resource, params) => {
    const { page, perPage } = params.pagination;
    const { field, order } = params.sort;

    const skip = (page - 1) * perPage;
    const limit = perPage;
    const sort = `${field} ${order}`;

    const query = {
      limit,
      skip,
      sort,
      where: JSON.stringify({
        ...params.filter,
        [params.target]: params.id,
      }),
    };

    const url = `${apiUrl}/${resource}?${stringify(query)}`;

    return httpClient(url).then(({ headers, json }) => {
      const contentRange = headers.get("content-range");
      let total = 0;
      if (contentRange) {
        const rangeParts = contentRange.split("/");
        if (rangeParts.length > 1) {
          total = parseInt(rangeParts[1], 10);
        } else {
          total = parseInt(contentRange, 10);
        }
        if (isNaN(total)) {
          console.warn(
            "Content-Range header found but total could not be parsed:",
            contentRange
          );
          total = json.length;
        }
      } else {
        console.warn(
          "No Content-Range header found, using json.length as total. Resource:",
          resource
        );
        total = json.length;
      }

      return {
        data: json,
        total,
      };
    });
  },

  update: async (resource, params) => {
    const processedData = await processFiles(params.data, resource);
    return httpClient(`${apiUrl}/${resource}/${params.id}`, {
      method: "PUT",
      body: JSON.stringify(processedData),
    }).then(({ json }) => ({
      data: json,
    }));
  },

  updateMany: (resource, params) => {
    const query = {
      filter: JSON.stringify({ id: params.ids }),
    };
    return httpClient(`${apiUrl}/${resource}?${stringify(query)}`, {
      method: "PUT",
      body: JSON.stringify(params.data),
    }).then(({ json }) => ({ data: json }));
  },

  create: async (resource, params) => {
    const processedData = await processFiles(params.data, resource);
    return httpClient(`${apiUrl}/${resource}`, {
      method: "POST",
      body: JSON.stringify(processedData),
    }).then(({ json }) => ({
      data: { ...processedData, id: json.id },
    }));
  },

  delete: (resource, params) =>
    httpClient(`${apiUrl}/${resource}/${params.id}`, {
      method: "DELETE",
    }).then(({ json }) => ({ data: json })),

  deleteMany: (resource, params) => {
    return Promise.all(
      params.ids.map((id) =>
        httpClient(`${apiUrl}/${resource}/${id}`, {
          method: "DELETE",
        })
      )
    ).then((responses) => ({ data: responses.map(({ json }) => json.id) }));
  },
};

export default dataProvider;